import type {
  AdminErrorEvent,
  AdminMetrics,
  AutoSpamItem,
  DetectorConfig,
  EmailAccountSummary,
  PipelineCoverageSummary,
  SpamDigestPreferences,
  WorkspaceSummary,
} from "@spamjam/contracts";

export const API_ORIGIN =
  import.meta.env.VITE_API_ORIGIN || "http://localhost:8787";

export class ApiError extends Error {
  constructor(
    public readonly code: string,
    message: string,
    public readonly status: number,
  ) {
    super(message);
  }
}

export async function api<T>(path: string, init: RequestInit = {}): Promise<T> {
  const response = await fetch(`${API_ORIGIN}${path}`, {
    ...init,
    credentials: "include",
    headers: {
      ...(init.body ? { "content-type": "application/json" } : {}),
      ...init.headers,
    },
  });
  const body = (await response.json().catch(() => ({}))) as {
    error?: { code?: string; message?: string };
  };
  if (!response.ok)
    throw new ApiError(
      body.error?.code ?? "request_failed",
      body.error?.message ?? "The request failed",
      response.status,
    );
  return body as T;
}

export const endpoints = {
  health: () =>
    api<{
      status: "ok";
      product: "FuckSpam";
      environment: "local-dev" | "production";
      protection: { mutationKillSwitchEnabled: boolean };
      integrations: {
        google: boolean;
        stripe: boolean;
        typesafe: boolean;
        email: boolean;
        localOtpStub: boolean;
      };
    }>("/v1/health"),
  session: () =>
    api<{ user: { id: string; email: string; name: string } }>("/v1/session"),
  adminSession: () =>
    api<{ user: { id: string; email: string; name: string } }>(
      "/v1/admin/session",
    ),
  adminMetrics: (from: string, to: string) =>
    api<{
      range: { from: string; to: string };
      metrics: AdminMetrics;
    }>(`/v1/admin/metrics?${new URLSearchParams({ from, to })}`),
  adminErrors: (from: string, to: string) =>
    api<{
      range: { from: string; to: string };
      errors: AdminErrorEvent[];
      cloudflareUrl: string;
    }>(`/v1/admin/errors?${new URLSearchParams({ from, to })}`),
  workspaces: () => api<{ workspaces: WorkspaceSummary[] }>("/v1/workspaces"),
  accounts: () =>
    api<{ accounts: EmailAccountSummary[] }>("/v1/email-accounts"),
  configs: () =>
    api<{ config: DetectorConfig }>("/v1/dev/system-detector-config").then(
      ({ config }) => ({ configs: [config] }),
    ),
  digestPreferences: () =>
    api<{ preferences: SpamDigestPreferences }>("/v1/spam-digest"),
  recentSpam: (
    cursor?: string | null,
    account?: string | null,
    decision?: string | null,
  ) => {
    const params = new URLSearchParams();
    if (cursor) params.set("cursor", cursor);
    if (account) params.set("account", account);
    if (decision) params.set("decision", decision);
    return api<{ items: AutoSpamItem[]; nextCursor: string | null }>(
      `/v1/auto-spam${params.size ? `?${params}` : ""}`,
    );
  },
  evaluationContribution: () =>
    api<{
      contribution: {
        id: string;
        emailAccountId: string;
        status: "queued" | "processing" | "submitted" | "failed";
        sampleCount: number;
        consentedAt: string;
        submittedAt: string | null;
      } | null;
    }>("/v1/evaluation-contribution"),
  evaluations: () =>
    api<{ runs: Array<Record<string, unknown>> }>("/v1/dev/evaluations"),
  coverage: () =>
    api<{ coverage: PipelineCoverageSummary }>("/v1/operations/coverage"),
  sessions: () =>
    api<{
      sessions: Array<{
        id: string;
        createdAt: string;
        updatedAt: string;
        expiresAt: string;
        userAgent: string | null;
        ipAddress: string | null;
        isCurrent: boolean;
      }>;
    }>("/v1/sessions"),
};
